import { ProjectInstance, TeamMember } from '../../types';
import type { DiagramState as DiagramStateType } from '../../types/diagram-types';
import { api } from './base';
import { qmsApi } from './admin';

export const projectsApi = {
  list: async () => {
    const response = await api.get('/projects');
    return response.data.data as ProjectInstance[];
  },

  get: async (id: string) => {
    const response = await api.get(`/projects/${id}`);
    return response.data.data as ProjectInstance;
  },

  create: async (
    name: string,
    answers?: Record<string, unknown>,
    options?: {
      projectCode?: string;
      projectTitle?: string;
      customer?: string;
      program?: string;
      certificationAuthority?: string;
      certificationBasis?: string[];
      projectType?: 'aerospace' | 'creative' | 'technical';
      seriesName?: string;
    }
  ) => {
    const response = await api.post('/projects', { 
      name, 
      answers,
      ...options
    });
    return response.data.data as ProjectInstance;
  },

  update: async (id: string, answers: Record<string, unknown>) => {
    const response = await api.patch(`/projects/${id}`, { answers });
    return response.data.data as ProjectInstance;
  },

  updateMetadata: async (
    id: string,
    metadata: {
      name?: string;
      projectCode?: string;
      projectTitle?: string;
      customer?: string;
      program?: string;
      certificationAuthority?: string;
      certificationBasis?: string[];
    }
  ) => {
    // Uses the standard PATCH endpoint - backend merges with existing project
    const response = await api.patch(`/projects/${id}`, metadata);
    return response.data.data as ProjectInstance;
  },

  checkCodeUnique: async (code: string) => {
    // Check if project code is unique by listing all projects
    // TODO: Add dedicated backend endpoint for efficiency
    try {
      const projects = await projectsApi.list();
      return !projects.some(p => p.projectCode?.toUpperCase() === code.toUpperCase());
    } catch {
      return true; // Assume unique on error
    }
  },

  delete: async (id: string) => {
    await api.delete(`/projects/${id}`);
  },

  // Document operations
  getDocuments: async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}/documents`);
    return response.data.data;
  },

  getDocument: async (projectId: string, documentNumber: string) => {
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`);
    return response.data.data;
  },

  registerDocument: async (
    projectId: string,
    sourceTemplate: string,
    documentTitle: string,
    projectSpecificTitle: string,
    author?: string
  ) => {
    const response = await api.post(`/projects/${projectId}/documents`, {
      sourceTemplate,
      documentTitle,
      projectSpecificTitle,
      author
    });
    return response.data.data;
  },

  createRevision: async (
    projectId: string,
    documentNumber: string,
    changeDescription: string,
    _generatedPath?: string,  // Deprecated - backend generates this
    author?: string
  ) => {
    const response = await api.post(
      `/projects/${projectId}/documents/${documentNumber}/revisions`,
      { changeDescription, author }
    );
    return response.data.data;
  },

  overrideRevision: async (projectId: string, documentNumber: string, newRevision: string) => {
    const response = await api.patch(`/projects/${projectId}/documents/${documentNumber}/revision`, {
      newRevision,
    });
    return response.data.data;
  },

  getChangeHistory: async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}/change-history`);
    return response.data.data;
  },

  // Document file operations
  getDocumentFileUrl: (projectId: string, documentNumber: string) => {
    return `/api/projects/${projectId}/documents/${documentNumber}/file`;
  },

  downloadDocument: async (projectId: string, documentNumber: string, format?: 'html' | 'pdf') => {
    // Log download
    qmsApi.logAccess(
      'current-user',
      'Current User',
      documentNumber,
      'certification',
      'downloaded'
    );
    
    try {
      // Get presigned URL from backend (with auth)
      const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
        params: { download: 'true', format: format || 'html' }
      });
      
      const data = response.data.data;
      const downloadUrl = data.downloadUrl;
      const fileName = data.fileName;
      
      if (downloadUrl) {
        // Create temporary link element to trigger download with proper filename
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || `${documentNumber}.${format || 'html'}`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Download URL not available');
      }
    } catch (err: unknown) {
      throw new Error(`Download failed: ${(err as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error || (err as Error).message}`);
    }
  },

  downloadRevision: (projectId: string, documentNumber: string, revision: string) => {
    const url = `/api/projects/${projectId}/documents/${documentNumber}/revisions/${revision}/file`;
    window.open(url, '_blank');
  },

  changeStatus: async (projectId: string, documentNumber: string, status: string, user?: string, comments?: string) => {
    const response = await api.patch(`/projects/${projectId}/documents/${documentNumber}/status`, {
      status,
      user,
      comments
    });
    return response.data.data;
  },

  exportProject: (projectId: string) => {
    window.location.href = `/api/projects/${projectId}/export`;
  },

  // Team Management
  listMyProjects: async () => {
    const response = await api.get('/projects/my-projects');
    return response.data.data as ProjectInstance[];
  },

  getTeamMembers: async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}/team`);
    return response.data.data as TeamMember[];
  },

  addTeamMember: async (projectId: string, userId: string, role: 'Owner' | 'Engineer' | 'Viewer') => {
    const response = await api.post(`/projects/${projectId}/team`, { userId, role });
    return response.data.data as TeamMember;
  },

  removeTeamMember: async (projectId: string, userId: string) => {
    await api.delete(`/projects/${projectId}/team/${userId}`);
  },

  updateTeamMemberRole: async (projectId: string, userId: string, role: 'Owner' | 'Engineer' | 'Viewer') => {
    const response = await api.patch(`/projects/${projectId}/team/${userId}`, { role });
    return response.data.data as TeamMember;
  },

  // Wiring Diagram operations
  listDiagrams: async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}/diagrams`);
    return response.data.data as WiringDiagram[];
  },

  getDiagram: async (projectId: string, diagramId: string) => {
    const response = await api.get(`/projects/${projectId}/diagrams/${diagramId}`);
    return response.data.data as WiringDiagram;
  },

  getDiagramState: async (projectId: string, diagramId: string) => {
    const response = await api.get(`/projects/${projectId}/diagrams/${diagramId}/state`);
    return response.data.data as DiagramState;
  },

  // Clear all elements from a diagram (devices, wires, crossings)
  clearDiagram: async (projectId: string, diagramId: string) => {
    const response = await api.post(`/projects/${projectId}/diagrams/${diagramId}/clear`);
    return response.data.data as {
      message: string;
      deletedDevices: number;
      deletedWires: number;
      deletedCrossings: number;
      duplicatesRemoved: number;
    };
  },

  // Validate diagram state and return issues
  validateDiagram: async (projectId: string, diagramId: string) => {
    const response = await api.get(`/projects/${projectId}/diagrams/${diagramId}/validate`);
    return response.data.data as {
      valid: boolean;
      issues: Array<{ type: string; severity: 'warning' | 'error'; message: string; details?: Record<string, unknown> }>;
      stats: {
        deviceCount: number;
        wireCount: number;
        crossingCount: number;
        uniqueDeviceIds: number;
        uniqueWireIds: number;
        duplicateDevices: string[];
        duplicateWires: string[];
        orphanedWires: string[];
      };
    };
  },

  createDiagram: async (projectId: string, name: string, description?: string, diagramStyle: 'boeing_ata' | 'airbus_ata' | 'default' = 'boeing_ata') => {
    const response = await api.post(`/projects/${projectId}/diagrams`, { name, description, diagramStyle });
    return response.data.data as WiringDiagram;
  },

  // Mermaid Diagram operations (UNIFIED: uses documents API with documentType filter)
  listMermaidDiagrams: async (projectId: string) => {
    // Fetch documents with documentType: 'mermaid_diagram' from the unified documents API
    const response = await api.get(`/projects/${projectId}/documents`, {
      params: { documentType: 'mermaid_diagram' }
    });
    const documents = response.data.data || [];
    // Map document response to MermaidDiagram structure for backward compatibility
    return documents.map((doc: Record<string, unknown>) => ({
      diagramId: doc.documentNumber as string,
      documentNumber: doc.documentNumber as string,
      documentTitle: (doc.metadata as Record<string, unknown>)?.title as string || doc.title as string || 'Untitled Diagram',
      mermaidType: ((doc.metadata as Record<string, unknown>)?.mermaidType as string) || 'flowchart',
      projectId: doc.projectId as string,
      createdAt: doc.createdAt as string,
      updatedAt: doc.updatedAt as string,
    })) as MermaidDiagram[];
  },

  // @deprecated Use documentsApi.get() with documentNumber instead
  getMermaidDiagram: async (projectId: string, diagramId: string) => {
    const response = await api.get(`/projects/${projectId}/mermaid-diagrams/${diagramId}`);
    return response.data.data as MermaidDiagram;
  },

  // @deprecated Use documentsApi.post() with documentType: 'mermaid_diagram' instead
  createMermaidDiagram: async (projectId: string, data: {
    title: string;
    description?: string;
    mermaidType: MermaidDiagramType;
    mermaidSource: string;
    sourceDataRef?: MermaidDiagram['sourceDataRef'];
  }) => {
    const response = await api.post(`/projects/${projectId}/mermaid-diagrams`, data);
    return response.data.data as MermaidDiagram;
  },

  // @deprecated Use documentsApi.saveJsonDocument() instead
  updateMermaidDiagram: async (projectId: string, diagramId: string, data: {
    title?: string;
    description?: string;
    mermaidSource?: string;
  }) => {
    const response = await api.patch(`/projects/${projectId}/mermaid-diagrams/${diagramId}`, data);
    return response.data.data as MermaidDiagram;
  },

  // @deprecated Mermaid rendering is now done client-side via mermaid.js in TipTap
  renderMermaidDiagram: async (projectId: string, diagramId: string) => {
    const response = await api.post(`/projects/${projectId}/mermaid-diagrams/${diagramId}/render`);
    return response.data.data as { svgUrl: string; s3Key: string };
  },
};

// Re-export DiagramState from types (from Neptune)
export type { DiagramState as DiagramStateImport } from '../../types/diagram-types';
export type DiagramState = DiagramStateType;

// Wiring Diagram style type
export type WiringDiagramStyle = 'boeing_ata' | 'airbus_ata' | 'default';

// Wiring Diagram type
export interface WiringDiagram {
  documentNumber: string;
  diagramId: string;
  revision: string;
  projectId: string;
  documentTitle: string;
  description: string;
  documentType: 'wiring_diagram';
  diagramStyle: WiringDiagramStyle;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deviceCount: number;
  wireCount: number;
  crossingCount: number;
}

// Mermaid Diagram types
export type MermaidDiagramType = 
  | 'flowchart' | 'sequence' | 'state' | 'er' | 'graph'
  | 'classDiagram' | 'gantt' | 'pie' | 'timeline'
  | 'mindmap' | 'quadrantChart' | 'requirement' | 'c4Context';

export interface MermaidDiagram {
  documentNumber: string;
  diagramId: string;
  revision: string;
  projectId: string;
  documentTitle: string;
  description: string;
  documentType: 'mermaid_diagram';
  mermaidType: MermaidDiagramType;
  mermaidSource: string;
  svgS3Key?: string;
  svgUrl?: string;
  sourceDataRef?: {
    diagramId?: string;
    queryType?: string;
    queryTimestamp?: string;
  };
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
